import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { ApiService } from 'src/app/services/api.service';
import { LocalStorageService } from 'ngx-webstorage';
import { HeadingService } from 'src/app/services/heading.service';
import { Router } from '@angular/router';
import { ChecklistDatabaseService } from './services/check-list-database.service';
import { TodoItemFlatNode } from './models/todo-item-flat-node';
import { TodoItemNode } from './models/todo-item-node';

@Component({
  selector: 'app-user-notification-settings',
  templateUrl: './user-notification-settings.component.html',
  styleUrls: ['./user-notification-settings.component.scss'],
  providers: [ChecklistDatabaseService]
})
export class UserNotificationSettingsComponent implements OnInit {

  @Input() userID = null;

  @Output() saveNotificationSetting = new EventEmitter<boolean>();

  medicalCenterID = null;
  medicalCenterName = null;

  notificationSettings = null;

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<TodoItemFlatNode, TodoItemNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<TodoItemNode, TodoItemFlatNode>();

  /** A selected parent node to be inserted */
  selectedParent: TodoItemFlatNode | null = null;

  /** The new item's name */
  newItemName = '';

  treeControlGlobal: FlatTreeControl<TodoItemFlatNode>;
  treeControlCardiac: FlatTreeControl<TodoItemFlatNode>;
  treeControlTech: FlatTreeControl<TodoItemFlatNode>;
  treeControlNews: FlatTreeControl<TodoItemFlatNode>;
  treeControlAutomatic: FlatTreeControl<TodoItemFlatNode>;

  treeFlattener: MatTreeFlattener<TodoItemNode, TodoItemFlatNode>;

  dataSourceGlobal: MatTreeFlatDataSource<TodoItemNode, TodoItemFlatNode>;
  dataSourceCardiac: MatTreeFlatDataSource<TodoItemNode, TodoItemFlatNode>;
  dataSourceTech: MatTreeFlatDataSource<TodoItemNode, TodoItemFlatNode>;
  dataSourceNews: MatTreeFlatDataSource<TodoItemNode, TodoItemFlatNode>;
  dataSourceAutomatic: MatTreeFlatDataSource<TodoItemNode, TodoItemFlatNode>;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<TodoItemFlatNode>(true /* multiple */);
  checklistSoundSelection = new SelectionModel<TodoItemFlatNode>(true /* multiple */);
  checklistVibrationSelection = new SelectionModel<TodoItemFlatNode>(true /* multiple */);

  protected database: ChecklistDatabaseService;
  protected localStorageService: LocalStorageService;
  protected heading$: HeadingService;
  protected router: Router;
  protected apiService: ApiService;

  constructor(protected injector: Injector) {
    this.database = this.injector.get(ChecklistDatabaseService);
    this.localStorageService = this.injector.get(LocalStorageService);
    this.heading$ = this.injector.get(HeadingService);
    this.router = this.injector.get(Router);
    this.apiService = this.injector.get(ApiService);

    this.heading$.title.next({ text: 'Notifications settings' });
    if (!this.userID) {
      this.userID = this.localStorageService.retrieve('loggedUser').UserID;
    }
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel,
      this.isExpandable, this.getChildren);

    this.treeControlGlobal = new FlatTreeControl<TodoItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSourceGlobal = new MatTreeFlatDataSource(this.treeControlGlobal, this.treeFlattener);

    this.treeControlCardiac = new FlatTreeControl<TodoItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSourceCardiac = new MatTreeFlatDataSource(this.treeControlCardiac, this.treeFlattener);

    this.treeControlTech = new FlatTreeControl<TodoItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSourceTech = new MatTreeFlatDataSource(this.treeControlTech, this.treeFlattener);

    this.treeControlNews = new FlatTreeControl<TodoItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSourceNews = new MatTreeFlatDataSource(this.treeControlNews, this.treeFlattener);

    this.treeControlAutomatic = new FlatTreeControl<TodoItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSourceAutomatic = new MatTreeFlatDataSource(this.treeControlAutomatic, this.treeFlattener);

    this.database.dataGlobalChange.subscribe(data => {
      this.dataSourceGlobal.data = data;
    });

    this.database.dataCardiacChange.subscribe(data => {
      this.dataSourceCardiac.data = data;
    });

    this.database.dataTechChange.subscribe(data => {
      this.dataSourceTech.data = data;
    });

    this.database.dataNewsChange.subscribe(data => {
      this.dataSourceNews.data = data;
    });

    this.database.dataAutomaticChange.subscribe(data => {
      this.dataSourceAutomatic.data = data;
    });
  }

  async ngOnInit() {

    const notificationSettings = await this.apiService.getUserNotificationSettings(this.userID).toPromise();
    if (notificationSettings.success == true) {
      this.notificationSettings = {
        GeneralNotificationSetting: notificationSettings.data.GeneralNotificationSetting.map((element) => {
          return {
            id: element.GeneralNotificationSettingType,
            selection: element.GeneralNotificationSettingValue,
            sound: element.GeneralNotificationSettingSound,
            vibration: element.GeneralNotificationSettingVibration,
          };
        }),
        UrgentAlertTemplateSetting: notificationSettings.data.UrgentAlertTemplateSetting.map((element) => {
          return {
            id: element.UrgentAlertTemplateSettingUrgentAlertTemplateID,
            selection: element.UrgentAlertTemplateSettingValue,
            sound: element.UrgentAlertTemplateSettingSound,
            vibration: element.UrgentAlertTemplateSettingVibration
          };
        }),
        NewsAnnotationSetting: notificationSettings.data.NewsAnnotationSetting.map((element) => {
          return {
            id: element.NewsAnnotationSettingNewsAnnotationID,
            selection: element.NewsAnnotationSettingValue,
            sound: element.NewsAnnotationSettingSound,
            vibration: element.NewsAnnotationSettingVibration
          };
        }
        )
      };
    } else {
      alert('Something went wrong!');
    }
  }

  getUserGlobalSettings(type) {
    const globalSettings = this.notificationSettings.GeneralNotificationSetting;
    if (globalSettings) {
      return globalSettings.find((element) => element.id == type);
    }
    return null;
  }

  getLevel = (node: TodoItemFlatNode) => node.level;

  isExpandable = (node: TodoItemFlatNode) => node.expandable;

  getChildren = (node: TodoItemNode): TodoItemNode[] => node.children;

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: TodoItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.item
      ? existingNode
      : new TodoItemFlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.id = node.id;
    flatNode.type = node.type;
    flatNode.expandable = !!node.children;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }
  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  todoItemSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControlCardiac.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);
  }

  todoItemSoundSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistSoundSelection.toggle(node);
    const descendants = this.treeControlCardiac.getDescendants(node);
    this.checklistSoundSelection.isSelected(node)
      ? this.checklistSoundSelection.select(...descendants)
      : this.checklistSoundSelection.deselect(...descendants);
  }

  todoItemVibrationSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistVibrationSelection.toggle(node);
    const descendants = this.treeControlCardiac.getDescendants(node);
    this.checklistVibrationSelection.isSelected(node)
      ? this.checklistVibrationSelection.select(...descendants)
      : this.checklistVibrationSelection.deselect(...descendants);
  }

  async submit() {
    const unselectedUrgent = this.getDeselectedUrgentAlertTemplates();
    const unselectedNews = this.getDeselectedNewsAnnotations();
    const unselectedGlobal = this.getDeselectedGlobal();
    const data = {
      UrgentAlertTemplateSetting: unselectedUrgent,
      NewsAnnotationSetting: unselectedNews,
      GeneralNotificationSetting: unselectedGlobal
    };
    const result = await this.apiService.saveUserNotificationSettings(this.userID, data).toPromise();
    if (result.success !== true) {
      alert('Something went wrong!');
    } else {
      if (this.router.url == '/users/notification-settings') {
        alert('Changes successfully saved');
      }
      this.saveNotificationSetting.next(true);
    }
  }

  getDeselectedUrgentAlertTemplates() {
    const unselected = [];
    for (let i = 0; i < this.treeControlCardiac.dataNodes.length; i++) {
      const nodeState = this.getNodeState(this.treeControlCardiac.dataNodes[i]);
      if (nodeState !== null) {
        unselected.push(nodeState);
      }
    }
    for (let i = 0; i < this.treeControlNews.dataNodes.length; i++) {
      const nodeState = this.getNodeState(this.treeControlNews.dataNodes[i]);
      if (nodeState !== null) {
        unselected.push(nodeState);
      }
    }
    for (let i = 0; i < this.treeControlTech.dataNodes.length; i++) {
      const nodeState = this.getNodeState(this.treeControlTech.dataNodes[i]);
      if (nodeState !== null) {
        unselected.push(nodeState);
      }
    }
    return unselected;
  }

  /** Get the current state of the node */
  getNodeState(node) {
    console.log('getNodeState', node);
    if (node.expandable) {
      return null;
    }
    const selected = this.checklistSelection.isSelected(node);
    const sound = this.checklistSoundSelection.isSelected(node);
    const vibration = this.checklistVibrationSelection.isSelected(node);
    if (!selected || !sound || !vibration) {
      return {
        id: node.id,
        type: node.type,
        selected: selected,
        sound: sound,
        vibration: vibration
      };
    }
    return null;

  }

  getDeselectedNewsAnnotations() {
    const unselected = [];
    for (let i = 0; i < this.treeControlAutomatic.dataNodes.length; i++) {
      const nodeState = this.getNodeState(this.treeControlAutomatic.dataNodes[i]);
      if (nodeState !== null) {
        unselected.push(nodeState);
      }
    }
    return unselected;
  }

  getDeselectedGlobal() {
    const unselected = [];
    for (let i = 0; i < this.treeControlGlobal.dataNodes.length; i++) {
      const nodeState = this.getNodeState(this.treeControlGlobal.dataNodes[i]);
      if (nodeState !== null) {
        unselected.push(nodeState);
      }
    }
    return unselected;
  }

}
